import { Routes, Route, Navigate, useLocation } from 'react-router';
import { lazy, Suspense, useEffect } from 'react';

import { trackPageView } from '@/features/analytics/googleTag';
import { MainLayout } from '@/components/layout/MainLayout';
import { paths } from '@/app/routes/paths';

const Home = lazy(() => import('@/pages/Home/Home'));
const About = lazy(() => import('@/pages/About/About'));
const ClosedDays = lazy(() => import('@/pages/About/pages/ClosedDays/ClosedDays'));
const ClosedDaysObon2025 = lazy(
  () => import('@/pages/About/pages/ClosedDays/pages/ClosedDaysObon2025/ClosedDaysObon2025')
);
const PriceRevision20261101 = lazy(
  () => import('@/pages/About/pages/PriceRevision20261101/PriceRevision20261101')
);
const News = lazy(() => import('@/pages/News/News'));
const OsteoporosisLecture = lazy(() => import('@/pages/OsteoporosisLecture/OsteoporosisLecture'));
const FirstVisiting = lazy(() => import('@/pages/FirstVisiting/FirstVisiting'));
const Info = lazy(() => import('@/pages/Info/Info'));
const Appointment = lazy(() => import('@/pages/Appointment/Appointment'));
const Access = lazy(() => import('@/pages/Access/Access'));
const Cerec = lazy(() => import('@/pages/Cerec/Cerec'));
const Kids = lazy(() => import('@/pages/Kids/Kids'));
const ChildcareService = lazy(() => import('@/pages/ChildcareService/ChildcareService'));
const Orthodontic = lazy(() => import('@/pages/Orthodontic/Orthodontic'));
const Whitening = lazy(() => import('@/pages/Whitening/Whitening'));
const Dentures = lazy(() => import('@/pages/Dentures/Dentures'));
const Implantation = lazy(() => import('@/pages/Implantation/Implantation'));
const CovidMeasures = lazy(() => import('@/pages/CovidMeasures/CovidMeasures'));
const FacilityStandards = lazy(() => import('@/pages/FacilityStandards/FacilityStandards'));
const Price = lazy(() => import('@/pages/Price/Price'));
const PaymentMachine = lazy(() => import('@/pages/PaymentMachine/PaymentMachine'));
const Recruitment = lazy(() => import('@/pages/Recruitment/Recruitment'));
const Jobs = lazy(() => import('@/pages/Recruitment/pages/Jobs/Jobs'));
const DentistFullTime = lazy(
  () => import('@/pages/Recruitment/pages/Jobs/pages/DentistFullTime/DentistFullTime')
);
const DentistPartTime = lazy(
  () => import('@/pages/Recruitment/pages/Jobs/pages/DentistPartTime/DentistPartTime')
);
const HygienistMaintenance = lazy(
  () => import('@/pages/Recruitment/pages/Jobs/pages/HygienistMaintenance/HygienistMaintenance')
);
const HygienistAssistant = lazy(
  () => import('@/pages/Recruitment/pages/Jobs/pages/HygienistAssistant/HygienistAssistant')
);
const DentalAssistantReceptionist = lazy(
  () =>
    import(
      '@/pages/Recruitment/pages/Jobs/pages/DentalAssistantReceptionist/DentalAssistantReceptionist'
    )
);
const DentalAssistant = lazy(
  () => import('@/pages/Recruitment/pages/Jobs/pages/DentalAssistant/DentalAssistant')
);
const Receptionist = lazy(
  () => import('@/pages/Recruitment/pages/Jobs/pages/Receptionist/Receptionist')
);
const StaffTrainingProgram = lazy(
  () => import('@/pages/Recruitment/pages/StaffTrainingProgram/StaffTrainingProgram')
);
const HygienistTrainingProgram = lazy(
  () =>
    import(
      '@/pages/Recruitment/pages/StaffTrainingProgram/pages/HygienistTrainingProgram/HygienistTrainingProgram'
    )
);
const IntroductionOfDrMaruhashi = lazy(
  () =>
    import(
      '@/pages/Recruitment/pages/StaffTrainingProgram/pages/HygienistTrainingProgram/IntroductionOfDrMaruhashi/IntroductionOfDrMaruhashi'
    )
);
const Events = lazy(() => import('@/pages/Recruitment/pages/Events/Events'));
const RecruitmentEvent20250420 = lazy(
  () =>
    import(
      '@/pages/Recruitment/pages/Events/pages/RecruitmentEvent20250420/RecruitmentEvent20250420'
    )
);
const Ems = lazy(() => import('@/pages/Ems/Ems'));
const Periodontal = lazy(() => import('@/pages/Periodontal/Periodontal'));
const NotFound = lazy(() => import('@/pages/NotFound/NotFound'));

const RouteLoading = () => (
  <MainLayout>
    <div
      className="flex min-h-[60vh] items-center justify-center"
      aria-busy="true"
      aria-live="polite"
    >
      <span className="text-muted-foreground">読み込み中…</span>
    </div>
  </MainLayout>
);

const App = () => {
  const location = useLocation();

  useEffect(() => {
    const pagePath = `${location.pathname}${location.search}`;

    trackPageView({
      path: pagePath,
      title: document.title,
      location: window.location.href,
    });
  }, [location.pathname, location.search]);

  return (
    <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path={paths.root} element={<Home />} />
        <Route path={paths.about.root}>
          <Route index element={<About />} />
          <Route path={paths.about.closedDays.root}>
            <Route index element={<ClosedDays />} />
            <Route path={paths.about.closedDays.obon2025} element={<ClosedDaysObon2025 />} />
          </Route>
          <Route path={paths.about.priceRevision20261101} element={<PriceRevision20261101 />} />
        </Route>
        <Route path={paths.news} element={<News />} />
        <Route path={paths.osteoporosisLecture} element={<OsteoporosisLecture />} />

        <Route path={paths.firstVisiting} element={<FirstVisiting />} />
        <Route path={paths.info} element={<Info />} />
        <Route path={paths.appointment} element={<Appointment />} />
        <Route path={paths.access} element={<Access />} />
        <Route path={paths.cerec} element={<Cerec />} />
        <Route path={paths.kids} element={<Kids />} />
        <Route path={paths.childcareService} element={<ChildcareService />} />
        <Route path={paths.orthodontic} element={<Orthodontic />} />
        <Route path={paths.whitening} element={<Whitening />} />
        <Route path={paths.dentures} element={<Dentures />} />
        <Route path={paths.implantation} element={<Implantation />} />

        <Route path={paths.covidMeasures} element={<CovidMeasures />} />
        <Route path={paths.facilityStandards} element={<FacilityStandards />} />
        <Route path={paths.price} element={<Price />} />
        <Route path={paths.paymentMachine} element={<PaymentMachine />} />

        <Route path={paths.recruitment.root}>
          <Route index element={<Recruitment />} />
          <Route path={paths.recruitment.jobs.root}>
            <Route index element={<Jobs />} />
            <Route path={paths.recruitment.jobs.dentistFullTime} element={<DentistFullTime />} />
            <Route path={paths.recruitment.jobs.dentistPartTime} element={<DentistPartTime />} />
            <Route
              path={paths.recruitment.jobs.hygienistMaintenance}
              element={<HygienistMaintenance />}
            />
            <Route
              path={paths.recruitment.jobs.hygienistAssistant}
              element={<HygienistAssistant />}
            />
            <Route
              path={paths.recruitment.jobs.dentalAssistantReceptionist}
              element={<DentalAssistantReceptionist />}
            />
            <Route path={paths.recruitment.jobs.dentalAssistant} element={<DentalAssistant />} />
            <Route path={paths.recruitment.jobs.receptionist} element={<Receptionist />} />
          </Route>
          <Route path={paths.recruitment.staffTrainingProgram.root}>
            <Route index element={<StaffTrainingProgram />} />
            <Route path={paths.recruitment.staffTrainingProgram.hygienistTrainingProgram.root}>
              <Route index element={<HygienistTrainingProgram />} />
              <Route
                path={
                  paths.recruitment.staffTrainingProgram.hygienistTrainingProgram
                    .introductionOfDrMaruhashi
                }
                element={<IntroductionOfDrMaruhashi />}
              />
            </Route>
          </Route>
          <Route path={paths.recruitment.event.root}>
            <Route index element={<Events />} />
            <Route
              path={paths.recruitment.event.recruitmentEvent20250420}
              element={<RecruitmentEvent20250420 />}
            />
          </Route>
        </Route>

        <Route path={paths.ems} element={<Ems />} />
        <Route path={paths.periodontal} element={<Periodontal />} />

        {/* /404 を直接叩いても NotFound 出す */}
        <Route path={paths.notFound} element={<NotFound />} />
        {/* どこにもマッチしなかったら /404 にリダイレクト */}
        <Route path="*" element={<Navigate to={paths.notFound} replace />} />
      </Routes>
    </Suspense>
  );
};

export default App;
